import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  CreditCard,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  Download,
  Settings,
  Wallet,
  Plus,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  X
} from 'lucide-react';
import { dashboardAPI } from '../../utils/api.js';
import { useToastContext } from '../../contexts/ToastContext.jsx';
import StripePaymentMethodModal from './StripePaymentMethodModal';
import './PaymentManagement.scss';
import { anonymizeName } from '../../utils/demoMode.js';

const PaymentManagement = ({ location, user }) => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [walletSettings, setWalletSettings] = useState({
    lowBalanceThresholdDollars: 100
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTechnicians: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [maxRewardAmount, setMaxRewardAmount] = useState(1000);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [fundingAmount, setFundingAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const STRIPE_PUBLISHABLE_KEY = 'pk_test_51LUyRcCltDkEZ60Xp5beKGb8SCuuOm9ALBop8z6bbK9PljjeJea0VoWwkNZaIszE4Ohw49C2P1fZPq7zLVH27ao800JnHQj2Ab';

  useEffect(() => {
    fetchWalletData();
    fetchPendingPayouts(1); // Start with page 1
    fetchPaymentMethods();
    fetchRewardSettings();
  }, [location]);

  useEffect(() => {
    // Set default payment method when payment methods are loaded
    if (paymentMethods.length > 0 && !selectedPaymentMethod) {
      const defaultMethod = paymentMethods.find(m => m.isDefault) || paymentMethods[0];
      setSelectedPaymentMethod(defaultMethod.id);
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const fetchWalletData = async () => {
    try {
      const response = await fetch(`/api/payments/company/wallet?location=${location}&user=${user}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success && result.data.wallet) {
        const walletData = result.data.wallet;
        setWallet(walletData);
        setWalletSettings({
          lowBalanceThresholdDollars: walletData.lowBalanceThreshold || 100
        });
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
      // Fallback to mock data if API fails
      setWallet({
        balance: 5000.00,
        balanceCents: 500000,
        fundingMode: 'manual',
        lowBalanceThreshold: 100,
        virtualAccountNumber: '****4567'
      });
    }
  };

  const fetchPendingPayouts = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`/api/payments/admin/pending-payouts?location=${location}&user=${user}&page=${page}&limit=20${searchParam}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setPendingPayouts(result.data.payouts || []);
        setPagination(result.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalTechnicians: 0,
          limit: 20,
          hasNextPage: false,
          hasPreviousPage: false
        });
      } else {
        console.warn('Failed to fetch pending payouts:', result.message);
        setPendingPayouts([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
      setPendingPayouts([]);
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(`/api/payments/company/payment-methods?location=${location}&user=${user}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setPaymentMethods(result.data.paymentMethods || []);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    }
  };

  const fetchRewardSettings = async () => {
    try {
      const result = await dashboardAPI.getRewardSettings();
      if (result.success && result.data) {
        setMaxRewardAmount(result.data.maxRewardAmount || 1000);
      }
    } catch (error) {
      console.error('Error fetching reward settings:', error);
      // Use default value of 1000 if settings can't be fetched
    }
  };

  const handleSelectTechnician = (techId) => {
    setSelectedTechnicians(prev => {
      if (prev.includes(techId)) {
        return prev.filter(id => id !== techId);
      }
      return [...prev, techId];
    });
  };

  const handleSelectAll = () => {
    if (selectedTechnicians.length === pendingPayouts.filter(p => p.canCashOut).length) {
      setSelectedTechnicians([]);
    } else {
      setSelectedTechnicians(pendingPayouts.filter(p => p.canCashOut).map(p => p.technicianId));
    }
  };

  const handleBatchPayout = async () => {
    if (selectedTechnicians.length === 0) {
      showWarning('Please select at least one technician');
      return;
    }

    // Process batch payout directly - user already clicked the button

    setProcessingPayout(true);
    try {
      // API call would go here
      setTimeout(() => {
        showSuccess(`Successfully processed payouts for ${selectedTechnicians.length} technicians`);
        setSelectedTechnicians([]);
        fetchPendingPayouts(pagination.currentPage, searchTerm);
        fetchWalletData();
        setProcessingPayout(false);
      }, 2000);
    } catch (error) {
      console.error('Error processing batch payout:', error);
      showError('Failed to process payouts');
      setProcessingPayout(false);
    }
  };

  const handleIndividualPayout = async (technicianId) => {
    const tech = pendingPayouts.find(p => p.technicianId === technicianId);
    if (!tech) return;

    // Process individual payout directly - user already clicked the button

    setProcessingPayout(true);
    try {
      // API call would go here
      setTimeout(() => {
        showSuccess(`Successfully processed payout for ${anonymizeName(tech.technicianName)}`);
        fetchPendingPayouts(pagination.currentPage, searchTerm);
        fetchWalletData();
        setProcessingPayout(false);
      }, 1500);
    } catch (error) {
      console.error('Error processing payout:', error);
      showError('Failed to process payout');
      setProcessingPayout(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // API call would go here
      showSuccess('Settings saved successfully');
      setShowSettings(false);
      fetchWalletData();
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings');
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const response = await fetch(`/api/payments/company/payment-methods/setup?location=${location}&user=${user}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success && result.data?.clientSecret) {
        // Open the Stripe modal with the client secret
        setStripeClientSecret(result.data.clientSecret);
        setShowStripeModal(true);
      } else {
        const errorMessage = result.message || 'Unknown error occurred';
        console.error('Payment method setup failed:', errorMessage);
        showError('Failed to create payment method setup: ' + errorMessage);
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
      
      if (error.name === 'NetworkError' || error.message.includes('fetch')) {
        showError('Network error. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        showError('Request timed out. Please try again.');
      } else {
        showError('Failed to add payment method. Please try again or contact support if the problem persists.');
      }
    }
  };

  const handlePaymentMethodSuccess = () => {
    // Refresh the payment methods list
    fetchPaymentMethods();
    // Close the modal
    setShowStripeModal(false);
    setStripeClientSecret(null);
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    // Remove payment method directly - user already clicked the button

    try {
      const response = await fetch(`/api/payments/company/payment-methods/${paymentMethodId}?location=${location}&user=${user}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        showSuccess('Payment method removed successfully');
        fetchPaymentMethods();
      } else {
        showError('Failed to remove payment method: ' + result.message);
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      showError('Failed to remove payment method');
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      const response = await fetch(`/api/payments/company/payment-methods/${paymentMethodId}/default?location=${location}&user=${user}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        showSuccess('Default payment method updated');
        fetchPaymentMethods();
      } else {
        showError('Failed to update default payment method: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating default payment method:', error);
      showError('Failed to update default payment method');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchPendingPayouts(newPage, searchTerm);
      setSelectedTechnicians([]); // Clear selections when changing pages
    }
  };

  const handleRefreshData = () => {
    fetchWalletData();
    fetchPendingPayouts(pagination.currentPage, searchTerm);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const newSearchTerm = searchInput.trim();
    setSearchTerm(newSearchTerm);
    setSelectedTechnicians([]); // Clear selections when searching
    fetchPendingPayouts(1, newSearchTerm); // Reset to page 1 when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedTechnicians([]);
    fetchPendingPayouts(1, ''); // Reset to page 1 with no search
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleConfirmFunding = () => {
    const amount = parseFloat(fundingAmount);
    
    if (!selectedPaymentMethod || !fundingAmount || amount <= 0) {
      showWarning('Please select a payment method and enter a valid amount');
      return;
    }

    setShowConfirmation(true);
  };

  const handleManualFunding = async () => {
    const amount = parseFloat(fundingAmount);
    
    setShowConfirmation(false);
    showInfo(`Processing $${amount.toFixed(2)} payment...`);

    try {
      const response = await fetch(`/api/payments/company/wallet/fund?location=${location}&user=${user}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          paymentMethodId: selectedPaymentMethod
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        showSuccess(`Successfully added $${amount.toFixed(2)} to your wallet`);
        setShowFundingModal(false);
        setFundingAmount(''); // Reset form
        setShowConfirmation(false); // Reset confirmation
        fetchWalletData(); // Refresh wallet balance
      } else {
        console.error('Backend error response:', result);
        showError('Failed to add funds: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding funds:', error);
      showError('Failed to add funds. Please try again.');
    }
  };


  const totalPending = pendingPayouts.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalSelected = selectedTechnicians.reduce((sum, id) => {
    const tech = pendingPayouts.find(p => p.technicianId === id);
    return sum + (tech?.totalAmount || 0);
  }, 0);

  return (
    <div className="payment-management">
      <motion.div
        className="config-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="header-content">
          <div className="logo-section">
            <DollarSign className="logo" />
            <div>
              <h1>Payment Management</h1>
              <p>Manage company wallet, process technician payouts, and configure payment methods</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="btn-refresh"
              onClick={handleRefreshData}
            >
              <RefreshCw size={18} /> Refresh
            </button>
          </div>
        </div>
      </motion.div>

      {/* Wallet Overview */}
      <motion.div
        className="wallet-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="wallet-card">
          <div className="wallet-header">
            <h3><Wallet size={20} /> Company Wallet</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="settings-btn"
                onClick={() => setShowPaymentMethods(!showPaymentMethods)}
                title="Manage Payment Methods"
              >
                <CreditCard size={18} />
              </button>
              <button 
                className="settings-btn"
                onClick={() => setShowSettings(!showSettings)}
                title="Wallet Settings"
              >
                <Settings size={18} />
              </button>
            </div>
          </div>
          
          {wallet && (
            <div className="wallet-content">
              <div className="balance-display">
                <div className="balance-label">Platform Balance</div>
                <div className="balance-amount">
                  ${wallet.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className="balance-subtitle">Available for transfers</div>
                {wallet.pendingBalance > 0 && (
                  <div className="pending-balance">
                    <Clock size={16} /> 
                    ${wallet.pendingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })} pending
                  </div>
                )}
                {paymentMethods.length > 0 && (
                  <div className="funding-actions">
                    <button 
                      className="btn-add-funds"
                      onClick={() => setShowFundingModal(true)}
                    >
                      <Plus size={16} /> Add Funds
                    </button>
                  </div>
                )}
              </div>

              <div className="wallet-stats">
                <div className="stat">
                  <span className="stat-label">Funding Mode</span>
                  <span className="stat-value">Manual Only</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Payment Methods</span>
                  <span className="stat-value">{paymentMethods.length} configured</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Virtual Account</span>
                  <span className="stat-value">{wallet.virtualAccountNumber || 'Not set'}</span>
                </div>
              </div>

              <div className="pending-summary">
                <div className="summary-item">
                  <TrendingUp size={18} className="icon-success" />
                  <span>Total Pending Payouts:</span>
                  <strong>${totalPending.toFixed(2)}</strong>
                </div>
                {wallet.balance < totalPending && (
                  <div className="insufficient-funds">
                    <AlertCircle size={16} />
                    Insufficient funds for all pending payouts
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Payment Methods Panel */}
        {showPaymentMethods && (
          <div className="settings-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h4>Payment Methods</h4>
              <button 
                className="btn-add-payment" 
                onClick={handleAddPaymentMethod}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} /> Add Payment Method
              </button>
            </div>
            
            {paymentMethods.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                <CreditCard size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>No payment methods configured</p>
                <p style={{ fontSize: '0.875rem' }}>Add a payment method to enable manual wallet funding</p>
              </div>
            ) : (
              <div className="payment-methods-list">
                {paymentMethods.map((method) => (
                  <div 
                    key={method.id} 
                    className="payment-method-item"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      background: method.isDefault ? '#f0f9ff' : '#ffffff'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                          {method.type === 'us_bank_account' ? (
                            <>
                              {method.bankName} •••• {method.last4}
                              <span style={{ 
                                marginLeft: '0.5rem', 
                                padding: '0.125rem 0.5rem',
                                background: '#d1fae5',
                                color: '#065f46',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase'
                              }}>
                                {method.accountType}
                              </span>
                            </>
                          ) : (
                            <>
                              {method.brand?.toUpperCase()} •••• {method.last4}
                              <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                                {method.expMonth}/{method.expYear}
                              </span>
                            </>
                          )}
                          {method.isDefault && (
                            <span style={{
                              marginLeft: '0.5rem',
                              padding: '0.125rem 0.5rem',
                              background: '#dbeafe',
                              color: '#1d4ed8',
                              borderRadius: '4px',
                              fontSize: '0.75rem'
                            }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          Added {new Date(method.createdAt).toLocaleDateString()} • 
                          <span style={{ 
                            color: method.status === 'verified' ? '#059669' : '#ca8a04',
                            marginLeft: '0.25rem'
                          }}>
                            {method.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {!method.isDefault && (
                        <button
                          onClick={() => handleSetDefaultPaymentMethod(method.id)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            border: '1px solid #d1d5db',
                            background: '#ffffff',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleRemovePaymentMethod(method.id)}
                        style={{
                          padding: '0.375rem',
                          border: '1px solid #fecaca',
                          background: '#fef2f2',
                          color: '#dc2626',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="settings-panel">
            <h4>Wallet Settings</h4>
            <div className="settings-form">
              <div className="form-group">
                <label>Low Balance Alert Threshold ($)</label>
                <input
                  type="number"
                  value={walletSettings.lowBalanceThresholdDollars}
                  onChange={(e) => setWalletSettings({
                    ...walletSettings,
                    lowBalanceThresholdDollars: parseFloat(e.target.value)
                  })}
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  You'll see a warning when your wallet balance drops below this amount
                </small>
              </div>
              <button className="btn-save" onClick={handleSaveSettings}>
                Save Settings
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Pending Payouts */}
      <motion.div
        className="payouts-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="section-header">
          <h3><Users size={20} /> Pending Payouts</h3>
          {selectedTechnicians.length > 0 && (
            <div className="selection-info">
              <span>{selectedTechnicians.length} selected</span>
              <span className="selected-amount">
                Total: ${totalSelected.toFixed(2)}
              </span>
              <button 
                className="btn-process-batch"
                onClick={handleBatchPayout}
                disabled={processingPayout}
              >
                <Send size={16} /> Process Selected
              </button>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-wrapper">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search technicians by name or email..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="search-input"
              />
              {(searchInput || searchTerm) && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="clear-search-btn"
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button type="submit" className="search-btn">
              Search
            </button>
          </form>
          {searchTerm && (
            <div className="search-results-info">
              <span>Searching for: "{searchTerm}"</span>
              <span className="results-count">
                {pagination.totalTechnicians} result{pagination.totalTechnicians !== 1 ? 's' : ''} found
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spinner" size={24} />
            Loading pending payouts...
          </div>
        ) : pendingPayouts.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>{searchTerm ? 'No technicians match your search' : 'No technicians found'}</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
              {searchTerm 
                ? `Try adjusting your search term "${searchTerm}" or clear the search to see all technicians.`
                : 'Active technicians will appear here once they are added to your account.'
              }
            </p>
          </div>
        ) : (
          <div className="payouts-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedTechnicians.length === pendingPayouts.filter(p => p.canCashOut).length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Technician</th>
                  <th>Email</th>
                  <th>Rewards</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Last Earning</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayouts.map(payout => (
                  <tr key={payout.technicianId} className={!payout.hasPendingEarnings ? 'no-earnings' : (!payout.canCashOut ? 'disabled' : '')}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTechnicians.includes(payout.technicianId)}
                        onChange={() => handleSelectTechnician(payout.technicianId)}
                        disabled={!payout.canCashOut}
                      />
                    </td>
                    <td>
                      <div className="technician-info">
                        {anonymizeName(payout.technicianName)}
                        {!payout.hasPendingEarnings && (
                          <span className="badge no-earnings-badge">No Earnings</span>
                        )}
                      </div>
                    </td>
                    <td>{payout.technicianEmail ? anonymizeName(payout.technicianEmail.split('@')[0]) + '@example.com' : ''}</td>
                    <td>{payout.rewardCount}</td>
                    <td className="amount">
                      ${payout.totalAmount.toFixed(2)}
                    </td>
                    <td>
                      <span className={`status status-${payout.paymentProfileStatus}`}>
                        {payout.paymentProfileStatus === 'active' ? (
                          <><CheckCircle size={14} /> Active</>
                        ) : payout.paymentProfileStatus === 'pending' ? (
                          <><Clock size={14} /> Setup Pending</>
                        ) : (
                          <><AlertCircle size={14} /> Not Setup</>
                        )}
                      </span>
                    </td>
                    <td>{payout.lastEarning ? new Date(payout.lastEarning).toLocaleDateString() : 'Never'}</td>
                    <td>
                      {payout.canCashOut ? (
                        <button
                          className="btn-payout"
                          onClick={() => handleIndividualPayout(payout.technicianId)}
                          disabled={processingPayout}
                        >
                          <Send size={14} /> Pay Out
                        </button>
                      ) : payout.totalAmount === 0 ? (
                        <span className="min-threshold">No earnings yet</span>
                      ) : payout.totalAmount < 50 ? (
                        <span className="min-threshold">Below $50 minimum</span>
                      ) : (
                        <span className="setup-required">Setup Required</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalTechnicians)} of {pagination.totalTechnicians} technicians
              </span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPreviousPage}
                title="First Page"
              >
                <ChevronsLeft size={16} />
              </button>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>
              
              <div className="pagination-pages">
                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const startPage = Math.max(1, pagination.currentPage - Math.floor(maxPagesToShow / 2));
                  const endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);
                  
                  if (startPage > 1) {
                    pages.push(
                      <button key={1} className="pagination-btn" onClick={() => handlePageChange(1)}>
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button 
                        key={i} 
                        className={`pagination-btn ${i === pagination.currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < pagination.totalPages) {
                    if (endPage < pagination.totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button key={pagination.totalPages} className="pagination-btn" onClick={() => handlePageChange(pagination.totalPages)}>
                        {pagination.totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
              </div>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNextPage}
                title="Last Page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Transaction History */}
      <motion.div
        className="history-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="section-header">
          <h3><Clock size={20} /> Recent Transactions</h3>
          <button className="btn-export">
            <Download size={16} /> Export CSV
          </button>
        </div>
        <div className="transactions-placeholder">
          <p>Transaction history will appear here once payouts are processed</p>
        </div>
      </motion.div>

      {/* Stripe Payment Method Modal */}
      <StripePaymentMethodModal
        isOpen={showStripeModal}
        onClose={() => {
          setShowStripeModal(false);
          setStripeClientSecret(null);
        }}
        onSuccess={handlePaymentMethodSuccess}
        clientSecret={stripeClientSecret}
        stripePublishableKey={STRIPE_PUBLISHABLE_KEY}
      />

      {/* Manual Funding Modal */}
      {showFundingModal && (
        <div className="modal-overlay">
          <div className="funding-modal">
            <div className="modal-header">
              <h3>Add Funds to Wallet</h3>
              <button 
                className="close-button"
                onClick={() => setShowFundingModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {!showConfirmation ? (
                <>
                  <div className="form-group">
                    <label>Amount ($)</label>
                    <input
                      type="text"
                      value={fundingAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        
                        // Allow empty value
                        if (value === '') {
                          setFundingAmount('');
                          return;
                        }
                        
                        // Only allow digits and one decimal point
                        const cleanValue = value.replace(/[^\d.]/g, '');
                        
                        // Handle decimal points - only allow one
                        const parts = cleanValue.split('.');
                        if (parts.length > 2) {
                          return; // Don't update if more than one decimal point
                        }
                        
                        // Limit decimal places to 2
                        if (parts[1] && parts[1].length > 2) {
                          return; // Don't update if more than 2 decimal places
                        }
                        
                        setFundingAmount(cleanValue);
                      }}
                      placeholder="Enter amount (e.g., 500 or 1000.50)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method</label>
                    <select
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.type === 'us_bank_account' ? (
                            `${method.bankName} •••• ${method.last4} (${method.accountType})`
                          ) : (
                            `${method.brand?.toUpperCase()} •••• ${method.last4}`
                          )}
                          {method.isDefault && ' (Default)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="funding-summary">
                    <div className="summary-row">
                      <span>Current Balance:</span>
                      <span>${(wallet?.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="summary-row">
                      <span>Amount to Add:</span>
                      <span>${fundingAmount || '0'}</span>
                    </div>
                    <div className="summary-row total">
                      <span>New Balance:</span>
                      <span>${((wallet?.balance || 0) + (parseFloat(fundingAmount) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="confirmation-view">
                  <div className="confirmation-icon">
                    <CreditCard size={48} style={{ color: '#2563eb' }} />
                  </div>
                  <h3>Confirm Payment</h3>
                  <p>You are about to add <strong>${parseFloat(fundingAmount).toFixed(2)}</strong> to your wallet.</p>
                  
                  <div className="confirmation-details">
                    <div className="detail-row">
                      <span>Payment Method:</span>
                      <span>
                        {(() => {
                          const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
                          return method?.type === 'us_bank_account' 
                            ? `${method.bankName} •••• ${method.last4}`
                            : `${method?.brand?.toUpperCase()} •••• ${method?.last4}`;
                        })()}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span>Amount:</span>
                      <span>${parseFloat(fundingAmount).toFixed(2)}</span>
                    </div>
                    <div className="detail-row total">
                      <span>New Balance:</span>
                      <span>${((wallet?.balance || 0) + parseFloat(fundingAmount)).toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <p className="confirmation-note">This charge will appear on your statement as "OVERIT".</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!showConfirmation ? (
                <>
                  <button 
                    className="btn-cancel"
                    onClick={() => {
                      setShowFundingModal(false);
                      setShowConfirmation(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-fund"
                    onClick={handleConfirmFunding}
                    disabled={!selectedPaymentMethod || !fundingAmount || parseFloat(fundingAmount) <= 0}
                  >
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn-cancel"
                    onClick={() => setShowConfirmation(false)}
                  >
                    Back
                  </button>
                  <button 
                    className="btn-fund btn-confirm"
                    onClick={handleManualFunding}
                  >
                    Confirm Payment of ${parseFloat(fundingAmount).toFixed(2)}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;
